import { useMemo } from 'react';
import { captureException } from '@sentry/react';
import { CustomError } from 'typescript/utilities/custom_error';
import getUuidByString from 'uuid-by-string';
import {
  getAutoCompleteCandidates,
  getAutoCompletePlaceDetails,
} from 'typescript/clients/solidshop/address_client';
import { useMutation } from '@tanstack/react-query';
import { useLocaleInfo } from 'typescript/context/locale_info';
import { MUTATION_KEYS } from 'typescript/constants/react_query';

export const USER_INPUT_VALUE = 'user-input';

const useAddressSuggestions = (orderNumber: string) => {
  const { countryCode } = useLocaleInfo();
  const sessionToken = useMemo(
    () => getUuidByString(orderNumber),
    [orderNumber],
  );

  const useGetAddressSuggestions = () => {
    const {
      mutate: getAddressSuggestions,
      data: suggestions,
      reset: resetSuggestions,
      ...rest
    } = useMutation({
      mutationKey: [MUTATION_KEYS.GET_ADDRESS_SUGGESTIONS],
      mutationFn: async (partialAddress: string) => {
        const response = await getAutoCompleteCandidates({
          partialAddress,
          countryCode,
          sessionToken,
        });

        return response.candidates;
      },
      onError: (error) => {
        captureException(
          new CustomError(MUTATION_KEYS.GET_ADDRESS_SUGGESTIONS, error),
          {
            extra: {
              orderNumber,
            },
          },
        );

        return [];
      },
    });

    return { getAddressSuggestions, suggestions, resetSuggestions, ...rest };
  };

  const useGetSuggestionDetails = () => {
    const { mutate: getSuggestionDetails, ...rest } = useMutation({
      mutationKey: [MUTATION_KEYS.GET_SUGGESTION_DETAILS],
      mutationFn: async ({ placeId }: { placeId: string }) => {
        const placeDetails = await getAutoCompletePlaceDetails({
          placeId,
          sessionToken,
        });

        return placeDetails;
      },
      onError: (error, variables) => {
        captureException(
          new CustomError(MUTATION_KEYS.GET_SUGGESTION_DETAILS, error),
          {
            extra: {
              variables: JSON.stringify(variables),
            },
          },
        );

        return null;
      },
    });

    return { getSuggestionDetails, ...rest };
  };

  return { useGetAddressSuggestions, useGetSuggestionDetails };
};

export default useAddressSuggestions;
